import styled from "@emotion/styled";
import { Box, IconButton } from "@mui/material";
import { Button } from "Components/_Shared/Buttons/Button";
import { ReactComponent as CalendarIcon } from "Components/_Shared/Icons/Assets/calendar.svg";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import { Popper } from "Components/_Shared/Popper";
import { useDateFnsLocale } from "Infrastructure/Hooks/DateFns/useDateFnsLocale";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { DateRangePicker as Picker, type RangeKeyDict } from "react-date-range";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

export type DateRange = {
  readonly startDate: Date | undefined;
  readonly endDate: Date | undefined;
};

type Props = {
  readonly title: string;
  readonly value: DateRange | null;
  readonly onChange: (range: DateRange) => void;
};

const DateCreatedTrigger = styled(Button)`
  text-transform: none;
  border-radius: ${(props) => props.theme.spacing(Radius.M)};
  margin-right: ${(props) => props.theme.spacing(Spacing.XS)};
  min-width: 200px;
  justify-content: flex-start;
`;

const StyledPicker = styled(Picker)`
  & * {
    font-family: Roboto;
    font-size: 14px;
  }

  .rdrCalendarWrapper {
    background: transparent;
  }

  .rdrDefinedRangesWrapper {
    display: none;
  }

  .rdrNextPrevButton {
    background: ${(props) => props.theme.palette.primary.light};
  }

  .rdrSelected, .rdrInRange, .rdrStartEdge, .rdrEndEdge, .rdrDayToday .rdrDayNumber span:after {
    background: ${(props) => props.theme.palette.primary.main};
  }

  .rdrMonthPicker select, .rdrYearPicker select {
    text-transform: capitalize;
    font-weight: 600;
  }

  .rdrWeekDay {
    text-transform: capitalize;
  }

  .rdrEndEdge {
    border-top-right-radius: ${(props) => props.theme.spacing(Radius.MAX)};
    border-bottom-right-radius: ${(props) => props.theme.spacing(Radius.MAX)};
  }

  .rdrStartEdge {
      border-top-left-radius: ${(props) => props.theme.spacing(Radius.MAX)};
      border-bottom-left-radius: ${(props) => props.theme.spacing(Radius.MAX)};
  }
`;

export const DateRangePicker: React.FC<Props> = ({ onChange, value, title }) => {
  const selectionKey = "selection";
  const initialSelection: DateRange = {
    startDate: undefined,
    endDate: new Date(""),
  };

  const locale = useDateFnsLocale();
  const [selection, setSelection] = useState<DateRange>(initialSelection);

  useEffect(() => {
    if (!value?.startDate && !value?.endDate) {
      setSelection({
        startDate: undefined,
        endDate: new Date(""),
      });
    }
  }, [value]);

  const handleChange = (range: RangeKeyDict) => {
    const { startDate, endDate } = range[selectionKey];

    setSelection({ startDate, endDate });
    onChange({ startDate, endDate });
  };

  const getButtonText = (): string => {
    const { startDate, endDate } = selection;
    if (startDate === endDate && !!startDate) {
      return format(startDate, "d. M. yyyy");
    }

    if (startDate && endDate) {
      return `${format(startDate, "d. M. yyyy")} - ${format(endDate, "d. M. yyyy")}`;
    }

    return title;
  };

  return (
    <Popper
      noBorder
      noPadding
      boxShadow={2}
      btnEl={
        <Box display="flex" alignItems="center">
          <DateCreatedTrigger color="secondary" startAdornment={<CalendarIcon />}>
            {getButtonText()}
          </DateCreatedTrigger>
          {(selection.startDate || selection.endDate === new Date("")) && (
            <IconButton
              sx={{ width: 36 }}
              onClick={(event) => {
                event.preventDefault();
                setSelection(initialSelection);
                onChange({ startDate: undefined, endDate: undefined });
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      }
    >
      <StyledPicker
        locale={locale}
        showDateDisplay={false}
        showPreview={false}
        weekStartsOn={0}
        ranges={[{ ...selection, key: selectionKey }]}
        onChange={handleChange}
      />
    </Popper>
  );
};
